import React, { useState, useRef, useEffect } from "react";
//import { useSnackbar } from 'notistack';
import { useNavigate, Link, useLocation } from "react-router-dom";
import useData from "../../Hooks/useData";
import { useSnackbar } from "notistack";
import { useToken } from "../../Hooks/useToken";
import { TextField, Typography, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import styles from "./Login.module.css";

//import bomLogo from "../Pages/img/baptists-on-mission-logo.png"
//import signInImg from "../Pages/img/sign-in-page-image.jpg"
import axios from "../../api/axios";

const LOGIN_PATH = "/api/login";

const Login = () => {
  const { auth, setAuth } = useData();
  const [token, setToken] = useToken();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  

 // Build the full path (pathname + search + hash) the user was headed to
// before RequireAuth redirected them here, so query params/anchors survive.
const fromLocation = location.state?.from;
const from = fromLocation
  ? `${fromLocation.pathname}${fromLocation.search || ""}${fromLocation.hash || ""}`
  : "/events";

// Redirect if user is already logged in (e.g. token was still valid when
// this page mounted) — send them to their original destination, not always /events.
useEffect(() => {
  if (auth) {
    navigate(from, { replace: true });
  }
}, [auth, navigate, from]);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Real-time validation functions
  const validateField = (fieldName, value) => {
    const newErrors = { ...errors };
    
    switch (fieldName) {
      case 'email':
        if (!value || value.trim() === '') {
          newErrors.email = 'Email is required';
        } else if (!validateEmail(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
      case 'password':
        if (!value || value.trim() === '') {
          newErrors.password = 'Password is required';
        } else {
          delete newErrors.password;
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid and clear errors when fields become valid
  useEffect(() => {
    const hasEmailError = !username || username.trim() === '' || !validateEmail(username);
    const hasPasswordError = !password || password.trim() === '';
    
    const isValid = !hasEmailError && !hasPasswordError;
    
    setIsFormValid(isValid);
    
    // Clear errors for valid fields
    const newErrors = { ...errors };
    if (!hasEmailError && errors.email) {
      delete newErrors.email;
    }
    if (!hasPasswordError && errors.password) {
      delete newErrors.password;
    }
    setErrors(newErrors);
  }, [username, password]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      enqueueSnackbar("Please fill out all required fields correctly.", { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(LOGIN_PATH, { username, password }, {
        headers: {'Content-Type':'application/json'},
        withCredentials: true,
      });
      
      const jwtToken = response.data.token;
      // Decode JWT payload to extract user and roles
      const payloadBase64 = jwtToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(payloadBase64));

      setToken(jwtToken);
      // Set auth with structured data: user, roles, and token
      setAuth({ user: payload.user, roles: payload.roles, token: jwtToken });
      
      enqueueSnackbar("Login successful! Welcome back.", { variant: 'success' });
      navigate(from, { replace: true });
      
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 401) {
        enqueueSnackbar("Invalid email or password.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else if (error.response?.status === 403) {
        enqueueSnackbar("Account is disabled. Please contact support.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else if (error.response?.status >= 500) {
        enqueueSnackbar("Server error occurred. Please try again later.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else if (!navigator.onLine) {
        enqueueSnackbar("No internet connection. Please check your connection and try again.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else {
        enqueueSnackbar("Login failed. Please try again or contact support if the problem persists.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.accountNav}>
        <Typography className={styles.navText}>
          Sharing God's Love in Word and Deed
        </Typography>
      </div>
      <div className={styles.CreateAccountMain}>
        <div id="app" className={styles.contentHalf}>
          <section className={styles.loginFormSection}>
            <img
              src="./Images/baptists-on-mission-logo.png"
              alt="Baptists on Mission Logo"
              className={styles.bomLogo}
            />
            <form onSubmit={handleSubmit} className={styles.loginForm}>
              <div className={styles.formGroup}>
                <Typography variant="formLabel" className={styles.requiredLabel}>
                  Email Address
                </Typography>
                <TextField
                  type="email"
                  id="email"
                  placeholder="Enter your email address"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    validateField('email', e.target.value);
                  }}
                  error={!!errors.email}
                  fullWidth
                />
                {errors.email && (
                  <Typography variant="caption" color="error" className={styles.errorText}>
                    {errors.email}
                  </Typography>
                )}
              </div>
              <div className={styles.formGroup}>
                <Typography variant="formLabel" className={styles.requiredLabel}>
                  Password
                </Typography>
                <TextField
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    validateField('password', e.target.value);
                  }}
                  error={!!errors.password}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((s) => !s)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {errors.password && (
                  <Typography variant="caption" color="error" className={styles.errorText}>
                    {errors.password}
                  </Typography>
                )}
              </div>
              <button
                type="submit"
                className="button-default button-full-width"
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
            <p className="problem-text">
              If you have any problems signing in, please contact us at (800)
              395-5102 ext 5596
            </p>
            <hr />
            <div className={styles.authLinks}>
              <p className="mb-3">
                <Link to="/forgot-password">Forgot Password?</Link>
              </p>
              <p className="mb-5">
                Don't have an account?{" "}
                <Link to="/createaccount">Create Account</Link>
              </p>
            </div>
          </section>
        </div>
        <img
          className={styles.createAccountImg}
          src="./Images/sign-in-page-image.jpg"
        />
      </div>
    </>
  );
};

export default Login;
